import { TableColumnsType, Tooltip } from 'antd';
import { TierBadge, GenericBadge } from '../../dataInputs/tiers/components';
import type {
  CyberHubItApplicationMetricsData,
  TierData,
} from '../types/it-applications';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import { CompletionPercentage, FactorsRenderer } from '../../cyber/components';
import { QualitativeFactorsRELC } from '../../cyber/enterprise-applications';
import { APPLICATION_TYPES } from '../../cyber/constants';
import { InfoCircleFilled } from '@ant-design/icons';

export const COLUMN_WIDTHS = {
  APPLICATION_NAME: 150,
  APPLICATION_TYPE: 100,
  DR_TIER: 100,
  SLA_COMPLIANT: 75,
  RTO_COMPLIANT: 75,
  MTO_COMPLIANT: 75,
  RPO_COMPLIANT: 75,
  ANNUAL_REVENUE_SUPPORTED: 200,
  BUSINESS_FUNCTIONS_REVENUE_SUPPORTED: 100,
  DRP: 50,
  QUALITATIVE_FACTORS: 200,
  ARROW_LINK: 50,
} as const;

const CSS_CLASSES = {
  PRIMARY_MEDIUM_TEXT: 'text-primary-100 font-medium',
} as const;

// Color constants for application type badges
const APPLICATION_TYPE_COLORS: Record<string, string> = {
  [APPLICATION_TYPES.ENTERPRISE]: '#437EF7', // Blue for Enterprise applications
  [APPLICATION_TYPES.LOCATIONS]: '#DF9000', // Orange for Location-specific applications
};

const renderApplicationName = (
  name: string,
  _record: CyberHubItApplicationMetricsData,
) => (
  <span className="flex items-center justify-start gap-2">
    <span className={`${CSS_CLASSES.PRIMARY_MEDIUM_TEXT} text-black`}>
      {name}
    </span>
    {_record.unmapped && (
      <Tooltip
        title={`${_record.unmapped ? 'Unmapped' : 'Mapped'} application`}
      >
        <InfoCircleFilled className="text-[#293241]" />
      </Tooltip>
    )}
  </span>
);

export const renderApplicationType = (applicationType: string) => {
  const badgeColor = APPLICATION_TYPE_COLORS[applicationType] || '#437EF7';
  const prettyName =
    applicationType.charAt(0).toUpperCase() +
    applicationType.slice(1).toLowerCase();
  return <GenericBadge name={prettyName} color={badgeColor} />;
};

const renderTier = (tier: TierData | null) => {
  if (!tier) {
    return (
      <span className={CSS_CLASSES.PRIMARY_MEDIUM_TEXT}>Not Provided</span>
    );
  }

  return <TierBadge color={tier.color} name={tier.name} />;
};

const createMultiLineTitle = (lines: string[]) => (
  <span>
    {lines.map((line, index) => (
      <span key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </span>
    ))}
  </span>
);

export const columns: TableColumnsType<CyberHubItApplicationMetricsData> = [
  {
    title: 'Application Name',
    dataIndex: 'name',
    key: 'name',
    width: COLUMN_WIDTHS.APPLICATION_NAME,
    align: 'left',
    sortOrder: 'ascend',
    sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
    render: renderApplicationName,
  },
  {
    title: 'Application Type',
    dataIndex: 'applicationType',
    key: 'applicationType',
    width: COLUMN_WIDTHS.APPLICATION_TYPE,
    align: 'center',
    render: renderApplicationType,
  },
  {
    title: ' Tier',
    dataIndex: 'tier',
    key: 'tier',
    width: COLUMN_WIDTHS.DR_TIER,
    align: 'center',
    render: renderTier,
  },
  {
    title: 'SLA',
    dataIndex: 'slaRatio',
    key: 'slaRatio',
    align: 'center',
    width: COLUMN_WIDTHS.SLA_COMPLIANT,
    render: (value: number) => (
      <CompletionPercentage percent={value} colorize={true} />
    ),
  },
  {
    title: 'RTO',
    dataIndex: 'rtoRatio',
    key: 'rtoRatio',
    align: 'center',
    width: COLUMN_WIDTHS.RTO_COMPLIANT,
    render: (value: number) => (
      <CompletionPercentage percent={value} colorize={true} />
    ),
  },
  {
    title: 'MTO',
    dataIndex: 'mtoRatio',
    key: 'mtoRatio',
    align: 'center',
    width: COLUMN_WIDTHS.MTO_COMPLIANT,
    render: (value: number) => (
      <CompletionPercentage percent={value} colorize={true} />
    ),
  },
  {
    title: 'RPO',
    dataIndex: 'rpoRatio',
    key: 'rpoRatio',
    align: 'center',
    width: COLUMN_WIDTHS.RPO_COMPLIANT,
    render: (value: number) => (
      <CompletionPercentage percent={value} colorize={true} />
    ),
  },
  {
    title: createMultiLineTitle(['Annual Revenue', 'Supported']),
    dataIndex: 'annualRevenue',
    key: 'annualRevenue',
    align: 'center',
    width: COLUMN_WIDTHS.ANNUAL_REVENUE_SUPPORTED,
    sorter: (a, b) =>
      tableSortingMethods.number(a.annualRevenue, b.annualRevenue),
    render: (value: number) => (
      <span className="custom-table--column">
        <FinancialData text={value} formatType="decimal" />
      </span>
    ),
  },
  {
    title: createMultiLineTitle(['Business Functions', 'Supported']),
    dataIndex: 'supportedBusinessFunctions',
    key: 'supportedBusinessFunctions',
    align: 'center',
    width: COLUMN_WIDTHS.BUSINESS_FUNCTIONS_REVENUE_SUPPORTED,
    sorter: (a, b) =>
      tableSortingMethods.number(
        a.supportedBusinessFunctions,
        b.supportedBusinessFunctions,
      ),
    render: (value: number) => (
      <span className="custom-table--column">{value}</span>
    ),
  },
  {
    title: 'DRP',
    dataIndex: 'drpInPlace',
    key: 'drpInPlace',
    align: 'center',
    width: COLUMN_WIDTHS.DRP,
    render: (value: boolean) => (
      <span className="custom-table--column">{value ? 'Yes' : 'No'}</span>
    ),
  },
  {
    title: createMultiLineTitle(['Qualitative Factors', 'Supported']),
    dataIndex: 'qualitativeFactors',
    key: 'qualitativeFactors',
    align: 'left',
    ellipsis: true,
    width: COLUMN_WIDTHS.QUALITATIVE_FACTORS,
    render: (factors: QualitativeFactorsRELC) => {
      return (
        <div className="custom-table--column space-x-1">
          <FactorsRenderer factors={factors} />
        </div>
      );
    },
  },
];
